import React, { useState, useEffect } from "react";
import { useDarkMode } from "../hooks/useDarkMode";
import PatientForm from "./PatientForm";
import OTPVerification from "./OTPVerification";
import BookingsTab from "./BookingsTab";
import { fetchPatientByPhone, fetchMasterLists } from "../Services/api";
import { useTranslation } from "react-i18next";
import { RefreshCcw, CalendarCheck, Clock, User, Hash } from "lucide-react";
import { FaMale, FaFemale, FaTransgenderAlt } from "react-icons/fa";
import Loader from './Loader';
import { toast } from "react-toastify";
export default function UnifiedForm({ onOtpVerified }) {
  const [showForm, setShowForm] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const { t, i18n } = useTranslation();
  const isScriptLang = i18n.language === "ml" || i18n.language === "kn";
  const isDark = useDarkMode();
  const [activeTab, setActiveTab] = useState("registration");
  const [patientId, setPatientId] = useState(localStorage.getItem("patientId") || null);
  useEffect(() => {
    setIsExistingUser(Boolean(prefillData?.id));
  }, [prefillData]);
  const [patientList, setPatientList] = useState([]); // list of patients fetched
  const [showPatientPopup, setShowPatientPopup] = useState(false); // control modal
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [masterLists, setMasterLists] = useState(() => {
    try {
      const raw = localStorage.getItem("masterLists");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
const handleCancel = () => {
  const storedPatient = JSON.parse(localStorage.getItem("patient"));

  if (!storedPatient && !patientId) {
    toast.warn(t("patientSelector.mustSelect") || "Please select a patient to continue.");
    return;
  }

  setShowPatientPopup(false);
};
  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem("patient"));
    setPrefillData(storedData);
  }, []);
  useEffect(() => {
    // Listen for "showPatientPopup" event from App dropdown
    const handleShowPatientPopup = (e) => {
      const patients = e.detail || [];
      if (patients.length > 0) {
        setPatientList(patients);
        setShowPatientPopup(true);
      }
    };

    window.addEventListener("showPatientPopup", handleShowPatientPopup);
    return () => window.removeEventListener("showPatientPopup", handleShowPatientPopup);
  }, []);


  const handleOtpSuccess = async (_notes, phone) => {
    try {
      setLoadingMessage("Loading Patients...");
      setLoading(true);
      const res = await fetchPatientByPhone(phone);
      const patients = res?.patients || [];

      if (patients.length === 0) {
        setPrefillData(null);
        localStorage.setItem("otpVerified", "true");
        setShowForm(true);
        if (onOtpVerified) onOtpVerified(null);
        return;
      }

      localStorage.setItem("patients", JSON.stringify(patients));

      if (patients.length === 1) {
        selectPatient(patients[0]);
      } else {
        setPatientList(patients);
        setShowPatientPopup(true);
      }

      if (onOtpVerified) onOtpVerified();
    } catch (err) {
      console.error("Patient lookup error:", err);
      setPrefillData(null);
    } finally {
      localStorage.setItem("otpVerified", "true");
      setShowForm(true); // render the form immediately
                  setLoading(false);

    }
  };
  // Scroll to top whenever the user switches tabs
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  const onBookingSuccess = () => {
    setActiveTab("bookings");
  };


  const selectPatient = (patient) => {
    localStorage.setItem("patient", JSON.stringify(patient));
    localStorage.setItem("patientId", patient?.id);
    setPatientId(patient?.id);
    setPrefillData(patient);
    setActiveTab("registration");
    setShowPatientPopup(false);

    // Now continue
    localStorage.setItem("otpVerified", "true");
    setShowForm(true);
    if (onOtpVerified) onOtpVerified();
  };


  const otpVerified = localStorage.getItem("otpVerified") === "true";

  return (
    <div className="relative min-h-screen font-sans">
      {loading && <Loader message={loadingMessage} />}

      {/* Watermark */}
      <img
        src="/Images/TriloLogo_TRSP.png"
        alt="Watermark"
        className="pointer-events-none select-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20 z-0 w-[70%] max-w-[500px]"
        style={{ userSelect: "none" }}
      />

      {/* Main content */}
      <div className="relative  max-w-4xl mx-auto p-4 md:p-6 space-y-6">

        {/* OTP Step */}
        {!otpVerified && <OTPVerification mode="register" onSuccess={handleOtpSuccess} />}

        {/* Tabs */}
        {otpVerified && (
          <div className="rounded-2xl p-4 md:p-5 space-y-4 transition-colors duration-300 relative overflow-visible"
               style={{
                 background: isDark ? "rgba(15,23,42,0.58)" : "rgba(255,255,255,0.76)",
                 border: "1px solid " + (isDark ? "rgba(255,255,255,0.11)" : "rgba(255,255,255,0.7)"),
                 boxShadow: isDark
                   ? "0 32px 64px rgba(0,0,0,0.45), 0 8px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.15)"
                   : "0 32px 72px rgba(15,40,120,0.22), 0 8px 24px rgba(15,40,120,0.12), inset 0 1px 0 rgba(255,255,255,1), inset 0 -1px 0 rgba(0,0,0,0.04)",
                 animation: "card-fade-in 0.5s ease-out backwards",
               }}>
            {/* Sliding Tabs — sticky so they stay visible while scrolling */}
            <div className="sticky top-0 z-20 pb-2 pt-1 -mx-4 md:-mx-5 px-4 md:px-5"
                 style={{
                   background: isDark ? "rgba(15,23,42,0.82)" : "rgba(255,255,255,0.82)",
                   backdropFilter: "blur(20px)",
                   WebkitBackdropFilter: "blur(20px)",
                   borderRadius: "0 0 16px 16px",
                   borderBottom: "1px solid " + (isDark ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.06)"),
                 }}>
            <div className="relative w-full max-w-md mx-auto rounded-2xl p-1"
                 style={{ background: "transparent" }}>
              <div
                className="absolute top-1 left-1 h-[calc(100%-0.5rem)] w-1/2 rounded-xl shadow-md transition-all duration-300"
                style={{
                  background: "linear-gradient(135deg, #2563eb, #0891b2)",
                  transform: activeTab === "bookings" ? "translateX(100%)" : "translateX(0%)"
                }}
              />
              <div className="relative flex gap-1">
                {[
                  { key: "registration", label: t("tabs.registration"), icon: <User size={14} /> },
                  { key: "bookings",     label: t("tabs.myBookings"),   icon: <CalendarCheck size={14} /> },
                ].map(({ key, label, icon }) => {
                  const isActive = activeTab === key;
                  return (
                    <button
                      key={key}
                      className="flex-1 text-center rounded-xl transition-all duration-300 overflow-hidden"
                      onClick={() => setActiveTab(key)}
                      style={{
                        touchAction: "manipulation",
                        fontWeight: isActive ? 800 : 500,
                        fontSize: isScriptLang
                          ? (isActive ? "0.65rem" : "0.6rem")
                          : (isActive ? "0.875rem" : "0.8rem"),
                        lineHeight: isScriptLang ? 1.2 : 1.4,
                        padding: isScriptLang ? "6px 4px" : "10px 4px",
                        minHeight: 44,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: isActive ? 1 : 0.65,
                        color: isActive
                          ? "#ffffff"
                          : isDark ? "rgba(255,255,255,0.4)" : "rgba(15,23,42,0.4)",
                        border: isActive
                          ? "none"
                          : isDark
                            ? "1.5px dashed rgba(255,255,255,0.22)"
                            : "1.5px dashed rgba(15,23,42,0.2)",
                        whiteSpace: isScriptLang ? "pre-line" : "nowrap",
                        textOverflow: isScriptLang ? "clip" : "ellipsis",
                      }}
                    >
                      <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        {icon}
                        <span>{label}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            </div>{/* end sticky wrapper */}

            {/* Forms / Bookings */}
            <div className="relative z-10">
              {activeTab === "registration" && (
                <PatientForm
                  prefillData={prefillData}
                  masterLists={masterLists}
                  onPatientRegistered={(id, data) => {
                    setPatientId(id);
                    setPrefillData(data);
                    localStorage.setItem("patientId", id);
                    localStorage.setItem("patient", JSON.stringify(data));
                  }}
                  onBookingSuccess={() => setActiveTab("bookings")}
                />
              )}
              {activeTab === "bookings" && patientId && (
                <BookingsTab patientId={patientId} onSwitchToRegistration={() => setActiveTab("registration")} />
              )}
            </div>
          </div>
        )}

        {/* Patient Popup */}
        {showPatientPopup && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center
                bg-black/40 backdrop-blur-sm p-4">
            <div className="p-5 rounded-3xl w-full max-w-md max-h-[90vh] overflow-auto animate-fadeIn"
               style={{
                 background: isDark ? "rgba(15,23,42,0.72)" : "rgba(255,255,255,0.88)",
                 border: "1px solid " + (isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.75)"),
                 boxShadow: isDark
                   ? "0 32px 64px rgba(0,0,0,0.5), 0 8px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)"
                   : "0 32px 64px rgba(15,40,120,0.2), 0 8px 24px rgba(15,40,120,0.1), inset 0 1px 0 rgba(255,255,255,1)",
                 backdropFilter: "blur(28px)",
                 WebkitBackdropFilter: "blur(28px)",
               }}>
              {/* Header */}
              <h2 className="text-lg font-extrabold mb-5 text-center tracking-tight"
                  style={{ letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
                {t("personal.selectPatientHeader")}
              </h2>

              {/* Patient List */}
              <div className="grid grid-cols-1 gap-4">
                {patientList.map((p) => {
                  const formattedDob = new Date(p.dob).toLocaleDateString("en-GB");
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => selectPatient(p)}
                      className="relative w-full text-left p-4 rounded-2xl overflow-hidden transition-all duration-200 group"
                      style={{
                        background: "var(--surface)",
                        border: "1.5px solid var(--border-accent)",
                        boxShadow: "var(--card-shadow)",
                        touchAction: "manipulation"
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 8px 20px rgba(37,99,235,0.12)";
                        e.currentTarget.style.borderColor = isDark ? "#3b82f6" : "rgba(147,197,253,0.9)";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.transform = "";
                        e.currentTarget.style.boxShadow = "var(--card-shadow)";
                        e.currentTarget.style.borderColor = "var(--border-accent)";
                      }}
                    >
                      {/* Accent Bar */}
                    {/* Accent bar matching app theme */}
<div className="absolute left-0 top-0 h-full w-1.5 rounded-l-2xl
     bg-gradient-to-b from-blue-500 via-cyan-400 to-teal-400" />


                      {/* Patient Details */}
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1 text-sm sm:text-base">
                          <Hash size={16} /> {p.patientId}

                        </span>
                       <div
                                               className="inline-flex items-center justify-center rounded-full px-2 py-2 mt-1"
                                               style={{
                                                 backgroundColor:
                                                   p.gender === "Male"
                                                     ? (isDark ? "#1e3a5f" : "#DBEAFE")
                                                     : p.gender === "Female"
                                                       ? (isDark ? "#450a0a" : "#FECACA")
                                                       : (isDark ? "#334155" : "#E5E7EB"),
                                                 lineHeight: 1,
                                               }}
                                             >
                                               {p.gender === "Male" ? (
                                                 <FaMale className="text-blue-700 w-4 h-4" />
                                               ) : p.gender === "Female" ? (
                                                 <FaFemale className="text-red-600 w-4 h-4" />
                                               ) : (
                                                 <FaTransgenderAlt className="text-gray-600 w-4 h-4" />
                                               )}
                                               <span className="ml-1 text-xs font-semibold text-gray-800 whitespace-nowrap">
                                                 {p.gender === "Male"
                                                   ? "Male"
                                                   : p.gender === "Female"
                                                     ? "Female"
                                                     : "Other"}
                                               </span>
                                             </div>

                      </div>

                      <div className="flex flex-col gap-1 text-xs sm:text-sm">
                        <p className="flex items-center gap-2 text-gray-700 dark:text-gray-200 min-w-0">
                          <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                            <User size={16} />
                          </span>
                          <span className="font-medium flex-shrink-0">{t("personal.fullName")}:</span>
                          <span className="truncate">{p.salutation} {p.name}</span>
                        </p>

                        <p className="flex items-center gap-2 text-gray-700 dark:text-gray-200 min-w-0">
                          <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                            <CalendarCheck size={16} />
                          </span>
                          <span className="font-medium flex-shrink-0">{t("personal.dob")}:</span>
                          <span className="truncate">{formattedDob}</span>
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Cancel Button */}
              <button
  className="mt-5 w-full py-3 rounded-xl font-bold text-white transition-all duration-200 active:scale-95"
  onClick={handleCancel}
  style={{
    background: "linear-gradient(135deg, #2563eb, #0891b2)",
    boxShadow: "0 4px 14px rgba(37,99,235,0.3)",
    touchAction: "manipulation",
    minHeight: 48
  }}
>
  {t("patientSelector.cancel")}
</button>

            </div>
          </div>
        )}

      </div>
    </div>

  );
}
